import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TEAMS } from "@/lib/teams";
import Avatar from "@/components/Avatar";
import { toast } from "sonner";
import { Loader2, Trophy, Crown, Flame, Target, TrendingUp, ArrowLeft, Shield } from "lucide-react";

function Stat({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="bg-[#141414] border border-white/10 p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${accent}`} />
        <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">{label}</span>
      </div>
      <div className={`font-['Bebas_Neue'] text-4xl tracking-wide leading-none ${accent}`}>{value}</div>
      {sub && <div className="text-xs text-white/40 mt-1">{sub}</div>}
    </div>
  );
}

export default function Profile() {
  const { username } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    setError("");
    api.get(`/profile/${encodeURIComponent(username)}`)
      .then((res) => setData(res.data))
      .catch((err) => setError(err?.response?.status === 404 ? "Player not found." : "Something went wrong. Please try again."))
      .finally(() => setLoading(false));
  }, [username]);

  const isMe = user && data && user.username === data.username;
  const accuracy = data && data.correct + data.wrong > 0
    ? Math.round((data.correct / (data.correct + data.wrong)) * 100) : null;

  const changeAvatar = async (team) => {
    try {
      await api.put("/profile/avatar", { team: team || null });
      setData((d) => ({ ...d, avatar: team || null }));
      toast.success(team ? `Avatar set to ${team}` : "Avatar cleared");
    } catch {
      toast.error("Could not update avatar");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <button onClick={() => navigate("/standings")} className="flex items-center gap-1.5 text-white/40 hover:text-white transition-colors duration-200 text-xs uppercase tracking-widest mb-6" data-testid="profile-back">
        <ArrowLeft className="w-4 h-4" /> Standings
      </button>

      {loading ? (
        <div className="flex items-center gap-2 text-white/50 py-20 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading profile…
        </div>
      ) : error ? (
        <div className="bg-[#141414] border border-white/10 px-6 py-16 text-center" data-testid="profile-notfound">
          <p className="font-['Bebas_Neue'] text-3xl tracking-tight">{error}</p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8" data-testid="profile-header">
            <div className="flex items-center gap-5">
              <Avatar team={data.avatar} username={data.username} size="lg" />
              <div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-[#E31837] mb-1">
                  Player Profile {isMe && "· You"}
                </div>
                <h1 className="font-['Bebas_Neue'] text-6xl tracking-tight leading-none">{data.username}</h1>
                {isMe && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">Team Avatar</span>
                    <select
                      value={data.avatar || ""}
                      onChange={(e) => changeAvatar(e.target.value)}
                      className="appearance-none bg-[#141414] border border-white/10 px-3 py-1.5 rounded-sm text-xs uppercase tracking-wider focus:ring-2 focus:ring-[#E31837] focus:outline-none"
                      data-testid="avatar-select"
                    >
                      <option value="">None</option>
                      {Object.keys(TEAMS).sort().map((t) => (
                        <option key={t} value={t}>{t} · {TEAMS[t].name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="font-['Bebas_Neue'] text-5xl tracking-wide text-[#EAB308] leading-none">#{data.rank}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">of {data.total_players}</div>
              </div>
              <div className="text-center">
                <div className="font-['Bebas_Neue'] text-5xl tracking-wide text-[#E31837] leading-none">{data.season_total}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">Season Pts</div>
              </div>
            </div>
          </div>

          <div className={`grid grid-cols-2 ${data.pick_mode === "combo" || data.covers > 0 ? "lg:grid-cols-5" : "lg:grid-cols-4"} gap-3 mb-8`}>
            <Stat icon={TrendingUp} label="Best Week" accent="text-[#22C55E]"
              value={data.best_week !== null ? data.best_week : "—"}
              sub={data.best_week_num ? `Week ${data.best_week_num}` : "No results yet"} />
            <Stat icon={Crown} label="Weeks Won" accent="text-[#EAB308]"
              value={data.weeks_won} sub="Sole top scorer" />
            <Stat icon={Flame} label="Lone Wolf Hits" accent="text-[#E31837]"
              value={data.lone_wolf_total} sub="Only correct pick" />
            <Stat icon={Target} label="Accuracy" accent="text-white"
              value={accuracy !== null ? `${accuracy}%` : "—"}
              sub={`${data.correct}-${data.wrong} record`} />
            {(data.pick_mode === "combo" || data.covers > 0) && (
              <div data-testid="profile-covers">
                <Stat icon={Shield} label="Covers" accent="text-[#EAB308]"
                  value={data.covers} sub={`+${data.cover_bonus} bonus pts`} />
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-3 mb-8" data-testid="profile-streaks">
            <div className="flex items-center gap-3 bg-[#141414] border border-white/10 px-5 py-3">
              <Flame className="w-5 h-5 text-[#F97316]" />
              <div>
                <div className="font-['Bebas_Neue'] text-3xl leading-none text-[#F97316]" data-testid="current-streak">{data.current_streak}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-0.5">Current Hot Run</div>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-[#141414] border border-white/10 px-5 py-3">
              <Flame className="w-5 h-5 text-[#E31837]" />
              <div>
                <div className="font-['Bebas_Neue'] text-3xl leading-none text-[#E31837]" data-testid="longest-streak">{data.longest_streak}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-0.5">Longest Streak</div>
              </div>
            </div>
            <div className="flex items-center text-xs text-white/40 max-w-xs">
              A streak counts back-to-back weeks finishing with net positive points.
            </div>
          </div>

          <h2 className="font-['Bebas_Neue'] text-3xl tracking-tight mb-3">WEEK BY WEEK</h2>
          <div className="flex flex-wrap gap-x-5 gap-y-1 mb-3 text-[11px] text-white/50" data-testid="awards-legend">
            <span className="inline-flex items-center gap-1.5"><Crown className="w-3.5 h-3.5 text-[#EAB308]" /> Week Winner ×{data.weeks_won}</span>
            <span className="inline-flex items-center gap-1.5"><Flame className="w-3.5 h-3.5 text-[#E31837]" /> Lone Wolf King ×{data.lone_wolf_king_count}</span>
            <span className="inline-flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5 text-[#22C55E]" /> Biggest Riser ×{data.biggest_riser_count}</span>
          </div>
          <div className="border border-white/10 overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-[#0A0A0A] text-[10px] uppercase tracking-[0.2em] text-white/40">
                  <th className="text-left px-4 py-3 border-b border-white/10">Week</th>
                  <th className="text-center px-3 py-3 border-b border-white/10">Record</th>
                  <th className="text-center px-3 py-3 border-b border-white/10">Base</th>
                  <th className="text-center px-3 py-3 border-b border-white/10">Lone Wolf</th>
                  <th className="text-center px-3 py-3 border-b border-white/10">Awards</th>
                  <th className="text-right px-4 py-3 border-b border-white/10">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.weekly.map((w) => {
                  const hasResults = w.decided > 0 && w.played;
                  return (
                    <tr key={w.week_number} className="hover:bg-white/5 transition-colors duration-150" data-testid={`profile-week-${w.week_number}`}>
                      <td className="px-4 py-2.5 border-b border-white/10 font-medium">Week {w.week_number}</td>
                      <td className="text-center px-3 py-2.5 border-b border-white/10 text-white/70">
                        {hasResults ? <><span className="text-[#22C55E]">{w.correct}</span>-<span className="text-[#EF4444]">{w.wrong}</span></> : <span className="text-white/20">–</span>}
                      </td>
                      <td className="text-center px-3 py-2.5 border-b border-white/10 tabular-nums">{hasResults ? w.base : <span className="text-white/20">–</span>}</td>
                      <td className="text-center px-3 py-2.5 border-b border-white/10">
                        {w.lone_wolf_count > 0 ? <span className="text-[#E31837]">+{w.lone_wolf} ({w.lone_wolf_count})</span> : <span className="text-white/20">–</span>}
                      </td>
                      <td className="text-center px-3 py-2.5 border-b border-white/10">
                        <span className="inline-flex items-center justify-center gap-1.5">
                          {w.week_winner && <Crown className="w-4 h-4 text-[#EAB308]" title="Week Winner" />}
                          {w.lone_wolf_king && <Flame className="w-4 h-4 text-[#E31837]" title="Lone Wolf King" />}
                          {w.biggest_riser && <TrendingUp className="w-4 h-4 text-[#22C55E]" title="Biggest Riser" />}
                          {!w.week_winner && !w.lone_wolf_king && !w.biggest_riser && <span className="text-white/20">–</span>}
                        </span>
                      </td>
                      <td className="text-right px-4 py-2.5 border-b border-white/10">
                        {hasResults ? (
                          <span className={`font-['Bebas_Neue'] text-xl tracking-wide ${w.total > 0 ? "text-[#22C55E]" : w.total < 0 ? "text-[#EF4444]" : "text-white/50"}`}>{w.total}</span>
                        ) : (
                          <span className="text-white/20">{w.played ? "0" : "no pick"}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
